import { getDashletRender } from 'ts-components/CustomBlockRender/dashletRenderRegistry';
import {
  CaptureError,
  NOT_RENDERED_GRACE_MS,
  captureElement,
  nextPaint,
  replyWithCapture,
} from 'ts-components/PiChat/capture/captureElement';
import type { CaptureOutcome } from 'ts-components/PiChat/capture/captureUpload';

const EXCLUDED_CAPTURE_SELECTORS = [
  '.dashlet-menu',
  '.drag-handle',
  '.react-resizable-handle',
] as const;
const EXCLUDED_SELECTOR = EXCLUDED_CAPTURE_SELECTORS.join(', ');

const captureFilter = (node: Node): boolean => {
  const element = node as Partial<Element>;
  return (
    typeof element.matches !== 'function' || !element.matches(EXCLUDED_SELECTOR)
  );
};

const findDashletCard = (element: HTMLElement, dashletId: string) => {
  const card = element.closest<HTMLElement>('[data-dashlet-id]');
  return card?.getAttribute('data-dashlet-id') === dashletId ? card : element;
};

const waitForSettledElement = async (
  dashletId: string,
  signal: AbortSignal
): Promise<HTMLElement> => {
  let missingSince = Date.now();
  while (!signal.aborted) {
    const entry = getDashletRender(dashletId);
    if (entry?.element.isConnected) {
      if (entry.settled) return entry.element;
      missingSince = Date.now();
    } else if (Date.now() - missingSince >= NOT_RENDERED_GRACE_MS) {
      throw new CaptureError(
        'not_rendered',
        `Dashlet ${dashletId} is not rendered on this page`
      );
    }
    await nextPaint();
  }
  throw new CaptureError('capture_failed', 'Capture was cancelled');
};

export const captureDashletForRequest = (
  dashletId: string,
  {
    signal,
    reply,
    waitForRefresh,
  }: {
    signal: AbortSignal;
    reply: (outcome: CaptureOutcome) => void;
    waitForRefresh: () => Promise<unknown>;
  }
): Promise<void> =>
  replyWithCapture(reply, async () => {
    await waitForRefresh().catch(() => undefined);
    const card = findDashletCard(
      await waitForSettledElement(dashletId, signal),
      dashletId
    );
    return captureElement(card, {
      name: `Dashlet ${dashletId}`,
      filter: captureFilter,
    });
  });
